import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/supabase-context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faSpinner, faCheck } from '@fortawesome/free-solid-svg-icons';

const Footer = () => {
  const { supabase } = useAuth();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Honeypot check - bots fill hidden fields, real users don't
      if (honeypot) {
        setIsSuccess(true);
        setEmail('');
        setTimeout(() => setIsSuccess(false), 3000);
        return;
      }

      // Validate email
      if (!email || !email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      // Get user agent for analytics
      const userAgent = navigator.userAgent;
      
      // Insert into newsletter_contacts table
      const { error: insertError } = await supabase
        .from('newsletter_contacts')
        .insert([
          {
            email: email.toLowerCase().trim(),
            source: 'footer',
            user_agent: userAgent
          }
        ]);

      if (insertError) {
        // Check if it's a duplicate email error
        if (insertError.code === '23505') {
          throw new Error('This email is already subscribed to our newsletter!');
        }
        throw insertError;
      }

      // Success!
      setIsSuccess(true);
      setEmail('');
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);

    } catch (err) {
      console.error('Newsletter signup error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Gap that matches footer gradient */}
      <div className="h-12 bg-gradient-to-r from-teal-100 to-purple-100 print:hidden"></div>
      <footer className="bg-gradient-to-r from-teal-100 to-purple-100 overflow-hidden mt-0 print:hidden" role="contentinfo">
      <div className="container mx-auto px-4 py-8">
        {/* Top section with logo and links */}
        <div className="flex flex-wrap justify-between gap-y-8">
          {/* Logo and description */}
          <div className="w-full md:w-1/3 lg:w-1/4 pr-8">
            <h3 className="text-xl font-bold text-brand-teal mb-3">KindlyListed</h3>
            <p className="text-brand-dark text-sm">Accessible Resource Directory for the IDD community.</p>
            
            {/* Social icons */}
            <div className="flex mt-4 space-x-3">
              <a 
                href="https://facebook.com/kindlylisted" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="Facebook"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm text-brand-teal hover:bg-brand-teal hover:text-white transition-colors"
              >
                <span className="sr-only">Facebook</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 2H15C13.6739 2 12.4021 2.52678 11.4645 3.46447C10.5268 4.40215 10 5.67392 10 7V10H7V14H10V22H14V14H17L18 10H14V7C14 6.73478 14.1054 6.48043 14.2929 6.29289C14.4804 6.10536 14.7348 6 15 6H18V2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a 
                href="https://x.com/kindlylisted" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="Twitter"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm text-brand-teal hover:bg-brand-teal hover:text-white transition-colors"
              >
                <span className="sr-only">Twitter</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23 3C22.0424 3.67548 20.9821 4.19211 19.86 4.53C19.2577 3.83751 18.4573 3.34669 17.567 3.12393C16.6767 2.90116 15.7395 2.9572 14.8821 3.28445C14.0247 3.61171 13.2884 4.1944 12.773 4.95372C12.2575 5.71303 11.9877 6.61234 12 7.53V8.53C10.2426 8.57557 8.50127 8.18581 6.93101 7.39545C5.36074 6.60508 4.01032 5.43864 3 4C3 4 -1 13 8 17C5.94053 18.398 3.48716 19.0989 1 19C10 24 21 19 21 7.5C20.9991 7.22145 20.9723 6.94359 20.92 6.67C21.9406 5.66349 22.6608 4.39271 23 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              {/* <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="Instagram"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm text-brand-teal hover:bg-brand-teal hover:text-white transition-colors"
              >
                <span className="sr-only">Instagram</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 2H7C4.23858 2 2 4.23858 2 7V17C2 19.7614 4.23858 22 7 22H17C19.7614 22 22 19.7614 22 17V7C22 4.23858 19.7614 2 17 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 11.37C16.1234 12.2022 15.9813 13.0522 15.5938 13.799C15.2063 14.5458 14.5931 15.1514 13.8416 15.5297C13.0901 15.9079 12.2384 16.0396 11.4078 15.9059C10.5771 15.7723 9.80976 15.3801 9.21484 14.7852C8.61991 14.1902 8.22773 13.4229 8.09406 12.5922C7.9604 11.7615 8.09206 10.9099 8.47032 10.1584C8.84858 9.40685 9.45418 8.79374 10.201 8.40624C10.9478 8.01874 11.7978 7.87659 12.63 8C13.4789 8.12588 14.2648 8.52146 14.8717 9.12831C15.4785 9.73515 15.8741 10.5211 16 11.37Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17.5 6.5H17.51" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a> */}
            </div>
            
            {/* <a 
              href="https://coff.ee/kindlylisted" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-block mt-6 px-6 py-2 bg-gradient-to-r from-[#4BB0A8] to-[#9256A0] text-white rounded-md hover:opacity-90 transition-opacity duration-200 font-medium shadow-sm"
            >
              Support Us
            </a> */}
          </div>
          
          {/* Quick links */}
          <div className="w-1/2 sm:w-auto">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-brand-purple mb-3">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-brand-dark hover:text-brand-teal transition-colors">Home</Link></li>
              <li><Link to="/businesses" className="text-brand-dark hover:text-brand-teal transition-colors">Businesses</Link></li>
              <li><Link to="/events" className="text-brand-dark hover:text-brand-teal transition-colors">Events</Link></li>
              <li><Link to="/resources" className="text-brand-dark hover:text-brand-teal transition-colors">Resources</Link></li>
              <li><Link to="/blog" className="text-brand-dark hover:text-brand-teal transition-colors">Blog</Link></li>
              <li><Link to="/partners" className="text-brand-dark hover:text-brand-teal transition-colors">Our Partners</Link></li>
            </ul>
          </div>
          
          {/* Resources */}
          <div className="w-1/2 sm:w-auto">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-brand-purple mb-3">Resources</h3>
            <ul className="space-y-2 text-sm">
              {/* <li><Link to="/suggest" className="text-brand-dark hover:text-brand-teal transition-colors">Suggest a Listing</Link></li> */}
              <li><Link to="/about" className="text-brand-dark hover:text-brand-teal transition-colors">About Us</Link></li>
              <li><Link to="/press" className="text-brand-dark hover:text-brand-teal transition-colors">Press & Media</Link></li>
              <li><Link to="/faq" className="text-brand-dark hover:text-brand-teal transition-colors">FAQ</Link></li>
              <li><Link to="/contact" className="text-brand-dark hover:text-brand-teal transition-colors">Contact Us</Link></li>
              <li><Link to="/developer-notes" className="text-brand-dark hover:text-brand-teal transition-colors">Developer Notes</Link></li>
            </ul>
          </div>

          {/* Newsletter Signup */}
          <div className="w-full md:w-auto md:min-w-[280px]">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-brand-purple mb-3">
              <FontAwesomeIcon icon={faEnvelope} className="mr-2" aria-hidden="true" />
              Stay Updated
            </h3>
            <p className="text-brand-dark text-sm mb-4">
              Get weekly updates on upcoming events, new businesses, and community news.
            </p>
            
            {isSuccess ? (
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="flex items-center text-green-600">
                  <FontAwesomeIcon icon={faCheck} className="mr-2" aria-hidden="true" />
                  <span className="text-sm font-medium">Successfully subscribed!</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-white"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                {/* Honeypot field - hidden from real users, bots will fill it */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                  <label htmlFor="footer-website">Website</label>
                  <input
                    type="text"
                    id="footer-website"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-2" role="alert" aria-live="assertive">
                    <p className="text-red-600 text-xs">{error}</p>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-4 py-2 bg-gradient-to-r from-[#4BB0A8] to-[#9256A0] text-white text-sm rounded-md hover:opacity-90 transition-opacity font-medium disabled:opacity-50 flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" aria-hidden="true" />
                      Subscribing...
                    </>
                  ) : (
                    'Subscribe'
                  )}
                </button>
                
                <p className="text-xs text-gray-500">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </form>
            )}
          </div>
        </div>
        
        {/* Bottom section with copyright and legal links */}
        <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center">
          <p className="text-brand-dark text-sm mb-4 sm:mb-0">&copy; {currentYear} KindlyListed. All rights reserved.</p>
          <div className="flex space-x-6">
            <a 
              href="/privacy-policy.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-brand-dark hover:text-brand-teal transition-colors"
            >
              Privacy Policy
            </a>
            <a 
              href="/terms-of-service.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-brand-dark hover:text-brand-teal transition-colors"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
